'use client';

import { c as _c } from "react/compiler-runtime";
import { useAddToWatchlist, useRemoveFromWatchlist } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { ShareButton } from '@/components/market/share-button';
import { AnimatedBookmark } from '@/components/ui/animated-bookmark';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { Copy01Icon, HelpCircleIcon } from '@/components/ui/icon/glyphs';
import { TOAST_IDS } from '@/config/toast-ids';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { useAuthenticatedWatchlist } from '@/hooks/use-authenticated-watchlist';
import { showToast } from '@/lib/ui/toast';
import { cn } from '@/lib/ui/utils';
import { copyWalletAddress } from '@/lib/wallet/copy-address';
import { useWinnerInfoModalStore } from '@/stores/winner-info-modal';
interface WinnerTokenActionsProps {
  marketPubkey: string;
  /** Winning-token SPL mint — copied to the clipboard + shared. */
  mint: string;
  /** Winning-token name — labels the copy toast + share card. */
  tokenName: string;
  priceD9: string;
  canonicalPath: string;
  className?: string;
}
function truncateMint(addr: string): string {
  if (addr.length <= 12) return addr;
  return `${addr.slice(0, 5)}...${addr.slice(-5)}`;
}

/**
 * Winner-page action row (Figma 3916-2745): watchlist toggle + copy the public
 * Solana address + share, sitting under the token stats card.
 */
export function WinnerTokenActions(t0) {
  const $ = _c(51);
  const {
    marketPubkey,
    mint,
    tokenName,
    priceD9,
    canonicalPath,
    className
  } = t0;
  const t = useTranslations("market.winnerDetail");
  const tMarket = useTranslations("market");
  const tWatch = useTranslations("common.watchlist");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      kind: "winners",
      fields: ["pubkeys"]
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    data: watchlist
  } = useAuthenticatedWatchlist(t1);
  const {
    ensureAuthenticated
  } = useAuthGate();
  const addToWatchlist = useAddToWatchlist();
  const removeFromWatchlist = useRemoveFromWatchlist();
  let t2;
  if ($[1] !== marketPubkey || $[2] !== watchlist?.pubkeys) {
    t2 = watchlist?.pubkeys?.includes(marketPubkey) ?? false;
    $[1] = marketPubkey;
    $[2] = watchlist?.pubkeys;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const isBookmarked = t2;
  const openInfo = useWinnerInfoModalStore(_temp);
  let t3;
  if ($[4] !== addToWatchlist || $[5] !== ensureAuthenticated || $[6] !== isBookmarked || $[7] !== marketPubkey || $[8] !== removeFromWatchlist || $[9] !== tWatch) {
    t3 = function toggleWatch() {
      if (!ensureAuthenticated()) {
        return;
      }
      if (isBookmarked) {
        removeFromWatchlist.mutate({
          kind: "winners",
          pubkey: marketPubkey
        });
      } else {
        addToWatchlist.mutate({
          kind: "winners",
          pubkey: marketPubkey
        });
      }
      showToast({
        id: isBookmarked ? TOAST_IDS.WATCHLIST_REMOVED : TOAST_IDS.WATCHLIST_SAVED,
        message: isBookmarked ? tWatch("removed") : tWatch("added"),
        variant: "success"
      });
    };
    $[4] = addToWatchlist;
    $[5] = ensureAuthenticated;
    $[6] = isBookmarked;
    $[7] = marketPubkey;
    $[8] = removeFromWatchlist;
    $[9] = tWatch;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const toggleWatch = t3;
  let t4;
  if ($[11] !== mint || $[12] !== tokenName) {
    t4 = function handleCopy() {
      if (mint !== "") {
        copyWalletAddress(mint, tokenName);
      }
    };
    $[11] = mint;
    $[12] = tokenName;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const handleCopy = t4;
  let t5;
  if ($[14] !== className) {
    t5 = cn("flex items-center gap-2", className);
    $[14] = className;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== tMarket) {
    t6 = tMarket("bookmarkLabel");
    $[16] = tMarket;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== isBookmarked || $[19] !== t6 || $[20] !== toggleWatch) {
    t7 = <AnimatedBookmark isSaved={isBookmarked} onClick={toggleWatch} ariaLabel={t6} variant="secondary" buttonSize="default" size={16} className="shrink-0 text-foreground-secondary" />;
    $[18] = isBookmarked;
    $[19] = t6;
    $[20] = toggleWatch;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t) {
    t8 = t("copyAddressLabel");
    $[22] = t;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const t9 = mint === "";
  let t10;
  if ($[24] !== mint) {
    t10 = truncateMint(mint);
    $[24] = mint;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== handleCopy || $[27] !== t10 || $[28] !== t8 || $[29] !== t9) {
    t11 = <AnimatedButton variant="secondary" size="default" prefix={Copy01Icon} aria-label={t8} className="flex-1 font-mono text-foreground-secondary" onClick={handleCopy} disabled={t9}>{t10}</AnimatedButton>;
    $[26] = handleCopy;
    $[27] = t10;
    $[28] = t8;
    $[29] = t9;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== canonicalPath || $[32] !== marketPubkey || $[33] !== mint || $[34] !== priceD9 || $[35] !== tokenName) {
    t12 = <ShareButton labeled={true} size="default" marketPubkey={marketPubkey} marketTitle={tokenName} canonicalPath={canonicalPath} winnerMint={mint} previewVersion={priceD9} className="shrink-0 text-foreground-secondary" />;
    $[31] = canonicalPath;
    $[32] = marketPubkey;
    $[33] = mint;
    $[34] = priceD9;
    $[35] = tokenName;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== t) {
    t13 = t("infoButtonLabel");
    $[37] = t;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== openInfo) {
    t14 = () => openInfo(true);
    $[39] = openInfo;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Icon icon={HelpCircleIcon} size={16} />;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== t13 || $[43] !== t14) {
    t16 = <AnimatedButton variant="secondary" size="default" aria-label={t13} className="shrink-0 text-foreground-secondary" onClick={t14}>{t15}</AnimatedButton>;
    $[42] = t13;
    $[43] = t14;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== t11 || $[46] !== t12 || $[47] !== t16 || $[48] !== t5 || $[49] !== t7) {
    t17 = <div className={t5}>{t7}{t11}{t12}{t16}</div>;
    $[45] = t11;
    $[46] = t12;
    $[47] = t16;
    $[48] = t5;
    $[49] = t7;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  return t17;
}
function _temp(s) {
  return s.setOpen;
}